/**
 * @fileoverview This file contains the ServiceWorkerChannelService, responsible for
 * managing communication channels with the Service Worker.
 */

import {EMPTY, filter, Observable, Subject} from 'rxjs';
import {Workbox} from 'workbox-window';

import {Injectable} from '@angular/core';

import {INewServiceWorkerMessage, isActionReturnResponse, isServiceWorkerMessage, ServiceWorkerActionResult, ServiceWorkerActionType} from '../../data';
import {IServiceWorkerEvent, isServiceWorkerEvent, ServiceWorkerEventType} from '../../data/service-worker-events.data';

@Injectable({
	providedIn: 'root',
})
export class ServiceWorkerChannelService {
	private wb: Workbox | null = null;
	private swMessages = new Subject<any>();
	private _swMessages = new Subject<IServiceWorkerEvent>();

	/* ---------------------------------------- Initialization --------------------------------------- */
	/**
	 * Initializes the service with a Workbox instance.
	 * @param workbox - The Workbox instance to use.
	 * @throws Error if Workbox instance is not set.
	 */
	public initialize(workbox: Workbox): void {
		this.wb = workbox;
		if (!this.wb) throw new Error('[ServiceWorkerChannelService] Workbox instance is not set');
		this.setupEventListeners();
	}

	/* --------------------------------------- Communication ---------------------------------------- */
	/**
	 * Sends a message to the Service Worker and returns an Observable for the response.
	 * @param message - The message to send to the Service Worker
	 * @returns An Observable that emits the response from the Service Worker
	 */
	public newSendMessageToSw<Action extends ServiceWorkerActionType>(message: INewServiceWorkerMessage<Action>): Observable<ServiceWorkerActionResult<Action>> {
		if (!isServiceWorkerMessage(message)) return EMPTY;
		const messageChannel = new MessageChannel();

		if (isActionReturnResponse(message.action)) {
			navigator.serviceWorker.ready.then(registration => {
				registration.active?.postMessage(message, [messageChannel.port2]);
			});

			return new Observable(observer => {
				messageChannel.port1.onmessage = event => {
					observer.next(event.data);
					observer.complete();
				};
			});
		} else {
			navigator.serviceWorker.ready.then(registration => {
				registration.active?.postMessage(message);
			});
			return EMPTY;
		}
	}

	/**
	 * Returns an Observable that emits messages from the Service Worker.
	 * @returns An Observable of messages from the Service Worker.
	 */
	public getMessagesFromSW(): Observable<any> {
		return this.swMessages.asObservable();
	}

	/**
	 * Returns an Observable that emits specific events from the Service Worker.
	 * @param swEventName - The name of the event to listen for
	 * @returns An Observable of specific Service Worker events
	 */
	public onEvent(swEventName: ServiceWorkerEventType): Observable<IServiceWorkerEvent> {
		return this._swMessages.pipe(filter(event => event.eventType === swEventName));
	}

	/* -------------------------------------- Event Listeners --------------------------------------- */
	/**
	 * Sets up event listeners for messages from the Service Worker.
	 */
	private setupEventListeners(): void {
		if (this.wb) {
			this.wb.addEventListener('message', event => {
				if (isServiceWorkerEvent(event.data)) {
					this._swMessages.next(event.data);
				}
			});
		}
	}
}
